import React, { createContext, FC, ReactNode, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';

import { createDirectionalSpacing, createDirectionalStyles } from '@/utils/createDirectionalStyles';

type Direction = 'ltr' | 'rtl';

export type PositionType = 'right' | 'left';

interface DirectionProviderProps {
  children: ReactNode;
}

const DirectionProviderContext = createContext<Direction>('ltr');

export const DirectionProvider: FC<DirectionProviderProps> = ({ children }) => {
  const { direction } = useDirection();
  const { i18n } = useTranslation('common');

  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = i18n.language;
  }, [i18n.language, direction]);

  return (
    <DirectionProviderContext.Provider value={direction}>
      <Helmet>
        <style type="text/css">{`body { direction: ${direction}; }`}</style>
      </Helmet>
      {children}
    </DirectionProviderContext.Provider>
  );
};

const toDirection = (directionString: string): Direction => (directionString === 'rtl' ? 'rtl' : 'ltr');

export const useDirection = () => {
  const { t } = useTranslation('common');
  const direction = toDirection(t('direction'));
  const { spacing } = useTheme();

  const isRtl = direction === 'rtl';
  const start: PositionType = isRtl ? 'right' : 'left';
  const end: PositionType = isRtl ? 'left' : 'right';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applyDirectionalStyles = useCallback(createDirectionalStyles(isRtl), [isRtl]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const directionalSpacing = useCallback(createDirectionalSpacing(isRtl, spacing), [isRtl, spacing]);

  return { direction, isRtl, start, end, applyDirectionalStyles, directionalSpacing };
};
